/**
 * Boot Pullback (Valid Pullback Flow) — motor puro de estructura y flujo.
 *
 * Sin React ni lightweight-charts: entran velas OHLC y sale la estructura
 * completa (swings, zigzag externo/interno, rupturas, niveles, flujo).
 *
 * Fases:
 *   1. Motor de retroceso válido -> stream de swings alternos.
 *   2. Clasificador de estructura (zigzag con filtro de ruptura de rango,
 *      anclas + refinamiento retroactivo, congelación de niveles).
 *   3. Máquina de flujo (long/short/transition con retardo de una pata).
 *   4. Estructura interna (pivotes internalPivotLen por el mismo clasificador).
 *   5. Dirección agregada para MTF (computeDirection).
 */

import { resolveConfig } from './constants.js'

// ─── Tiempo ────────────────────────────────────────────────────────────────

export function toSeconds(t) {
  return t > 1e10 ? Math.floor(t / 1000) : t
}

/**
 * Normaliza cualquier time de vela a segundos UNIX.
 * Acepta segundos, milisegundos y strings 'YYYY-MM-DD' (timeframes 1d/1w/1M).
 */
export function cleanTime(t) {
  if (t == null || t === '') return null
  if (typeof t === 'string') {
    const ms = new Date(t).getTime()
    if (Number.isNaN(ms)) return null
    return Math.floor(ms / 1000)
  }
  if (Number.isNaN(t) || !Number.isFinite(t)) return null
  return toSeconds(t)
}

// ─── Validación y normalización ─────────────────────────────────────────────

function emptyStructures() {
  return {
    points: [],
    breaks: [],
    levels: [],
    extH: null,
    extL: null,
    flowLegs: [],
    flow: null,
    transition: false,
  }
}

function emptyResult(error) {
  return {
    error,
    swings: [],
    external: emptyStructures(),
    internal: null,
    dashboard: {
      bias: 'undefined',
      externalSwings: 0,
      breaksCount: 0,
      lastEvent: null,
    },
    stateTable: {
      direction: 'undefined',
      directionSuffix: '',
      lastStructureDir: null,
      extH: null,
      extL: null,
      prevExtH: null,
      prevExtL: null,
      ifBreakUp: { label: null, effect: 'Sin flujo establecido' },
      ifBreakDown: { label: null, effect: 'Sin flujo establecido' },
      reversalCondition: 'Dos rupturas consecutivas en el mismo sentido',
    },
    _candles: [],
  }
}

function validateCandles(raw) {
  if (!Array.isArray(raw) || raw.length < 10) {
    return { code: 'INSUFFICIENT_DATA', message: `Se necesitan al menos 10 velas (recibidas: ${Array.isArray(raw) ? raw.length : 0})` }
  }
  for (let i = 0; i < raw.length; i++) {
    const c = raw[i]
    if (!c || cleanTime(c.time) == null) {
      return { code: 'INVALID_CANDLE', message: `Vela ${i}: time inválido` }
    }
    const { open, high, low, close } = c
    if (![open, high, low, close].every(Number.isFinite)) {
      return { code: 'INVALID_CANDLE', message: `Vela ${i}: OHLC nulo o no finito` }
    }
    if (high < low) {
      return { code: 'INVALID_CANDLE', message: `Vela ${i}: high < low` }
    }
    if (Math.max(open, close) > high || Math.min(open, close) < low) {
      return { code: 'INVALID_CANDLE', message: `Vela ${i}: open/close fuera del rango high/low` }
    }
  }
  return null
}

function normalizeCandles(raw) {
  const mapped = raw.map(c => ({
    time: cleanTime(c.time),
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
  }))
  mapped.sort((a, b) => a.time - b.time)
  const out = []
  for (const c of mapped) {
    if (out.length > 0 && out[out.length - 1].time === c.time) out[out.length - 1] = c
    else out.push(c)
  }
  return out
}

// ─── Fase 1: motor de retroceso válido ─────────────────────────────────────

export function computeSwings(candles, triggerOnWick = true) {
  const swings = []
  const n = candles.length
  if (n < 2) return swings

  let ref = 0
  let phase = null
  let trigger = null
  let start = -1

  // Seed: primera vela que rompe el rango de la referencia por un solo lado.
  for (let i = 1; i < n; i++) {
    const c = candles[i]
    const r = candles[ref]
    if (c.high > r.high && c.low < r.low) {
      ref = i // outside bar: la referencia se mueve sin arrancar fase
      continue
    }
    if (c.high > r.high) {
      phase = 'bull'
      ref = i
      trigger = c.low
      start = i + 1
      break
    }
    if (c.low < r.low) {
      phase = 'bear'
      ref = i
      trigger = c.high
      start = i + 1
      break
    }
  }
  if (phase === null) return swings

  for (let i = start; i < n; i++) {
    const c = candles[i]
    const r = candles[ref]
    if (phase === 'bear') {
      const newLow = c.low < r.low
      const hit = triggerOnWick ? c.high >= trigger : c.close > trigger
      if (newLow && hit) {
        // Outside bar: no confirma nada, solo reasigna referencia y trigger.
        ref = i
        trigger = c.high
      } else if (newLow) {
        ref = i
        trigger = c.high
      } else if (hit) {
        swings.push({
          type: 'low',
          price: r.low,
          time: r.time,
          refIndex: ref,
          confirmIndex: i,
          confirmTime: c.time,
        })
        phase = 'bull'
        ref = i
        trigger = c.low
      }
    } else {
      const newHigh = c.high > r.high
      const hit = triggerOnWick ? c.low <= trigger : c.close < trigger
      if (newHigh && hit) {
        ref = i
        trigger = c.low
      } else if (newHigh) {
        ref = i
        trigger = c.low
      } else if (hit) {
        swings.push({
          type: 'high',
          price: r.high,
          time: r.time,
          refIndex: ref,
          confirmIndex: i,
          confirmTime: c.time,
        })
        phase = 'bear'
        ref = i
        trigger = c.high
      }
    }
  }
  return swings
}

// ─── Fase 4 (stream): pivotes para la estructura interna ───────────────────

export function computePivots(candles, len = 2) {
  const out = []
  const n = candles.length
  for (let i = len; i < n - len; i++) {
    let isHigh = true
    let isLow = true
    for (let j = i - len; j <= i + len; j++) {
      if (j === i) continue
      if (candles[j].high >= candles[i].high) isHigh = false
      if (candles[j].low <= candles[i].low) isLow = false
    }
    const confirmIndex = i + len
    if (isHigh) {
      out.push({
        type: 'high',
        price: candles[i].high,
        time: candles[i].time,
        refIndex: i,
        confirmIndex,
        confirmTime: candles[confirmIndex].time,
      })
    }
    if (isLow) {
      out.push({
        type: 'low',
        price: candles[i].low,
        time: candles[i].time,
        refIndex: i,
        confirmIndex,
        confirmTime: candles[confirmIndex].time,
      })
    }
  }
  return out
}

// ─── Fase 3: máquina de flujo ──────────────────────────────────────────────

/**
 * Recorre direcciones de ruptura y devuelve el estado del flujo tras cada
 * break. legColor aplica el retardo de una pata en los flips: la pata de la
 * segunda contrarruptura conserva el color del flujo saliente.
 */
export function runFlow(dirs) {
  let flow = null
  let transition = false
  let consecDir = null
  let consecCount = 0
  const states = []
  for (const dir of dirs) {
    if (dir === consecDir) consecCount++
    else {
      consecDir = dir
      consecCount = 1
    }
    const oldFlow = flow
    let flipped = false
    if (flow === null) {
      if (consecCount >= 2) {
        flow = consecDir === 'up' ? 'long' : 'short'
        transition = false
      }
    } else if (flow === 'long') {
      if (dir === 'up') transition = false
      else if (transition) {
        flow = 'short'
        transition = false
        flipped = true
      } else transition = true
    } else {
      if (dir === 'down') transition = false
      else if (transition) {
        flow = 'long'
        transition = false
        flipped = true
      } else transition = true
    }
    let legColor = null
    if (flow !== null) {
      if (flipped) legColor = oldFlow
      else legColor = transition ? 'transition' : flow
    }
    states.push({ flow, transition, flipped, legColor })
  }
  return { flow, transition, states }
}

// ─── Fase 2: clasificador de estructura ────────────────────────────────────

export function buildStructure(swings, candles, opts = {}) {
  const maxFrozen = Number.isFinite(opts.maxFrozenLevels) ? opts.maxFrozenLevels : 60
  const points = []
  const breaks = []
  const levels = []
  let extH = null
  let extL = null
  let pending = [] // swings filtrados desde el último punto (candidatos a ancla)

  if (!Array.isArray(swings) || swings.length === 0) {
    return { points, breaks, levels, extH, extL, flowLegs: [], flow: null, transition: false }
  }

  const first = swings[0]
  points.push({
    type: first.type,
    price: first.price,
    time: first.time,
    index: first.refIndex,
    isAnchor: false,
    remnant: null,
    breakIndex: null,
  })
  const firstRef = { price: first.price, time: first.time, index: first.refIndex }
  if (first.type === 'high') extH = firstRef
  else extL = firstRef

  for (let si = 1; si < swings.length; si++) {
    const s = swings[si]
    const lastPoint = points[points.length - 1]
    const upBreak = s.type === 'high' && (extH == null || s.price > extH.price)
    const downBreak = s.type === 'low' && (extL == null || s.price < extL.price)
    if (!upBreak && !downBreak) {
      pending.push(s)
      continue
    }

    let anchor = null
    if (lastPoint.type === s.type) {
      // Mismo tipo que el último punto: hace falta una ancla del tipo opuesto
      // para no romper la alternancia del zigzag.
      const needType = upBreak ? 'low' : 'high'
      const cands = pending.filter(p =>
        p.type === needType && p.refIndex > lastPoint.index && p.refIndex <= s.refIndex)
      let turn = null
      for (const p of cands) {
        if (turn == null) turn = p
        else if (needType === 'low' && p.price < turn.price) turn = p
        else if (needType === 'high' && p.price > turn.price) turn = p
      }
      let aPrice
      let aTime
      let aIndex
      let remnant = null
      if (turn) {
        aPrice = turn.price
        aTime = turn.time
        aIndex = turn.refIndex
        // Refinamiento retroactivo: el extremo verdadero puede estar en las
        // velas crudas posteriores al swing candidato.
        for (let j = aIndex + 1; j <= s.refIndex && j < candles.length; j++) {
          const better = needType === 'low' ? candles[j].low < aPrice : candles[j].high > aPrice
          if (better) {
            remnant = { time: aTime, price: aPrice }
            aPrice = needType === 'low' ? candles[j].low : candles[j].high
            aTime = candles[j].time
            aIndex = j
          }
        }
      } else {
        // Sin candidato acumulado: extremo directo de las velas crudas.
        let jBest = null
        for (let j = lastPoint.index + 1; j <= s.refIndex && j < candles.length; j++) {
          if (jBest == null) jBest = j
          else if (needType === 'low' && candles[j].low < candles[jBest].low) jBest = j
          else if (needType === 'high' && candles[j].high > candles[jBest].high) jBest = j
        }
        if (jBest != null) {
          aPrice = needType === 'low' ? candles[jBest].low : candles[jBest].high
          aTime = candles[jBest].time
          aIndex = jBest
        } else {
          aPrice = needType === 'low' ? s.price : s.price
          aTime = s.time
          aIndex = s.refIndex
        }
      }
      anchor = {
        type: needType,
        price: aPrice,
        time: aTime,
        index: aIndex,
        isAnchor: true,
        remnant,
        breakIndex: breaks.length,
      }
      points.push(anchor)
      if (upBreak) extL = { price: aPrice, time: aTime, index: aIndex }
      else extH = { price: aPrice, time: aTime, index: aIndex }
    }

    const prevExt = upBreak ? extH : extL
    const point = {
      type: s.type,
      price: s.price,
      time: s.time,
      index: s.refIndex,
      isAnchor: false,
      remnant: null,
      breakIndex: breaks.length,
    }
    points.push(point)
    if (upBreak) extH = { price: s.price, time: s.time, index: s.refIndex }
    else extL = { price: s.price, time: s.time, index: s.refIndex }

    if (prevExt != null) {
      levels.push({
        side: upBreak ? 'H' : 'L',
        price: prevExt.price,
        fromTime: prevExt.time,
        toTime: s.confirmTime,
        frozen: true,
      })
      if (levels.length > maxFrozen) levels.splice(0, levels.length - maxFrozen)
    }

    breaks.push({
      dir: upBreak ? 'up' : 'down',
      brokenLevel: prevExt ? { price: prevExt.price, time: prevExt.time } : null,
      breakTime: s.confirmTime,
      breakIndex: s.confirmIndex,
      newPrice: s.price,
      pointTime: s.time,
      anchor,
      label: null,
    })
    pending = []
  }

  // Nomenclatura CHoCH/BOS: misma dirección que el break anterior -> BOS,
  // dirección opuesta -> CHoCH. El primer break etiquetado es BOS.
  let anyLabeled = false
  for (let k = 0; k < breaks.length; k++) {
    const b = breaks[k]
    if (!b.brokenLevel) continue
    if (!anyLabeled) b.label = 'BOS'
    else b.label = (breaks[k - 1] && breaks[k - 1].dir === b.dir) ? 'BOS' : 'CHoCH'
    anyLabeled = true
    b.labelPrice = b.brokenLevel.price
    b.labelFromTime = b.brokenLevel.time
    b.labelToTime = b.breakTime
  }

  // Flujo + patas de sombreado
  const { flow, transition, states } = runFlow(breaks.map(b => b.dir))
  const flowLegs = []
  for (let k = 0; k < points.length - 1; k++) {
    const p0 = points[k]
    const p1 = points[k + 1]
    const st = p1.breakIndex != null ? states[p1.breakIndex] : null
    let top = -Infinity
    let bottom = Infinity
    const from = Math.min(p0.index, p1.index)
    const to = Math.max(p0.index, p1.index)
    for (let i = from; i <= to && i < candles.length; i++) {
      if (candles[i].high > top) top = candles[i].high
      if (candles[i].low < bottom) bottom = candles[i].low
    }
    if (!Number.isFinite(top) || !Number.isFinite(bottom)) continue
    flowLegs.push({
      fromIndex: from,
      toIndex: to,
      fromTime: p0.time,
      toTime: p1.time,
      top,
      bottom,
      color: st ? st.legColor : null,
    })
  }

  return { points, breaks, levels, extH, extL, flowLegs, flow, transition }
}

// ─── Tabla de estado ───────────────────────────────────────────────────────

function flowName(flow) {
  return flow === 'long' ? 'Long' : flow === 'short' ? 'Short' : null
}

function buildStateTable(external) {
  const { flow, transition, breaks, levels, extH, extL } = external
  const labeled = breaks.filter(b => b.label)
  const lastBreak = breaks.length > 0 ? breaks[breaks.length - 1] : null
  const lastLabeled = labeled.length > 0 ? labeled[labeled.length - 1] : null

  const direction = flow === null ? 'undefined' : transition ? 'transition' : flow
  const directionSuffix = transition
    ? '(transition)'
    : (lastLabeled && lastLabeled.label === 'CHoCH' ? '· CHoCH' : '')

  const prevExtH = [...levels].reverse().find(l => l.side === 'H') || null
  const prevExtL = [...levels].reverse().find(l => l.side === 'L') || null

  const labelFor = (dir) => {
    if (!lastLabeled) return 'BOS'
    return lastLabeled.dir === dir ? 'BOS' : 'CHoCH'
  }

  const simulate = (dir) => {
    const sim = runFlow([...breaks.map(b => b.dir), dir])
    if (sim.flow === null) return 'Sin flujo establecido'
    if (sim.flow !== flow) return `Cambia el flujo a ${flowName(sim.flow)}`
    if (sim.transition && !transition) return 'Deja el flujo en transición'
    if (sim.transition) return 'Mantiene la transición'
    return `Mantiene el flujo ${flowName(sim.flow)}`
  }

  let reversalCondition
  if (flow === 'long') {
    reversalCondition = transition
      ? 'Una ruptura bajista más del Ext L (segunda contrarruptura)'
      : 'Dos rupturas bajistas consecutivas del Ext L'
  } else if (flow === 'short') {
    reversalCondition = transition
      ? 'Una ruptura alcista más del Ext H (segunda contrarruptura)'
      : 'Dos rupturas alcistas consecutivas del Ext H'
  } else {
    reversalCondition = 'Dos rupturas consecutivas en el mismo sentido'
  }

  return {
    direction,
    directionSuffix,
    lastStructureDir: lastBreak ? lastBreak.dir : null,
    extH: extH ? { price: extH.price, time: extH.time } : null,
    extL: extL ? { price: extL.price, time: extL.time } : null,
    prevExtH: prevExtH ? { price: prevExtH.price, time: prevExtH.fromTime } : null,
    prevExtL: prevExtL ? { price: prevExtL.price, time: prevExtL.fromTime } : null,
    ifBreakUp: { label: labelFor('up'), effect: simulate('up') },
    ifBreakDown: { label: labelFor('down'), effect: simulate('down') },
    reversalCondition,
  }
}

// ─── compute() ─────────────────────────────────────────────────────────────

export function compute(rawCandles, config) {
  const cfg = resolveConfig(config)
  const validationError = validateCandles(rawCandles)
  if (validationError) {
    const res = emptyResult(validationError)
    res._cfg = cfg
    return res
  }

  const candles = normalizeCandles(rawCandles)
  const triggerOnWick = cfg.triggerOnWick ?? true

  const swings = computeSwings(candles, triggerOnWick)
  const external = buildStructure(swings, candles, { maxFrozenLevels: cfg.maxFrozenLevels })

  let internal = null
  if (cfg.showInternalStructure) {
    const pivots = computePivots(candles, cfg.internalPivotLen ?? 2)
    internal = buildStructure(pivots, candles, { maxFrozenLevels: cfg.maxFrozenLevels })
    internal.intH = internal.extH
    internal.intL = internal.extL
  }

  const labeled = external.breaks.filter(b => b.label)
  const lastLabeled = labeled.length > 0 ? labeled[labeled.length - 1] : null
  const direction = external.flow === null
    ? 'undefined'
    : external.transition ? 'transition' : external.flow

  const dashboard = {
    bias: direction,
    externalSwings: external.points.length,
    breaksCount: labeled.length,
    lastEvent: lastLabeled
      ? { label: lastLabeled.label, dir: lastLabeled.dir, price: lastLabeled.labelPrice, time: lastLabeled.breakTime }
      : null,
  }

  return {
    error: null,
    swings,
    external,
    internal,
    dashboard,
    stateTable: buildStateTable(external),
    _candles: candles,
    _cfg: cfg,
  }
}

// ─── Fase 5: dirección agregada (MTF) ──────────────────────────────────────

export function computeDirection(rawCandles, config) {
  const result = compute(rawCandles, config)
  if (result.error) return 'undefined'
  return result.dashboard.bias
}
